import type { Note } from '../shared/types.ts';
import type { DB } from './db.ts';

// One shared <audio> element. Mobile Safari only lets a page start sound from
// inside a tap; once an element has played from a tap it stays unlocked, so
// reusing it is what lets the next card autoplay after you rate this one.
const el = typeof Audio !== 'undefined' ? new Audio() : (null as unknown as HTMLAudioElement);
if (el) {
  el.preload = 'auto';
  el.preservesPitch = true;
}

const urls = new Map<string, string>();
let db: DB | null = null;

export function initAudio(d: DB): void {
  db = d;
}

async function urlFor(hash: string): Promise<string | null> {
  const hit = urls.get(hash);
  if (hit) return hit;
  const row = await db?.get('audio', hash);
  if (!row) return null;
  const url = URL.createObjectURL(row.blob);
  urls.set(hash, url);
  return url;
}

/** Load clips into object URLs ahead of time, so playback starts instantly. */
export function warm(hashes: string[]): void {
  for (const h of hashes) void urlFor(h);
}

// Settles the promise of whatever is playing now, so a sequence (native, then
// yours) awaiting it moves on when interrupted instead of hanging.
let finishCurrent: (() => void) | null = null;

export function stop(): void {
  el.pause();
  const f = finishCurrent;
  finishCurrent = null;
  f?.();
  if (typeof speechSynthesis !== 'undefined') speechSynthesis.cancel();
}

function playUrl(url: string, rate: number): Promise<void> {
  stop();
  el.src = url;
  // Loading a new src resets playbackRate to the default, so set both.
  el.defaultPlaybackRate = rate;
  el.playbackRate = rate;
  return new Promise((resolve) => {
    const finish = () => {
      el.removeEventListener('ended', finish);
      el.removeEventListener('error', finish);
      if (finishCurrent === finish) finishCurrent = null;
      resolve();
    };
    finishCurrent = finish;
    el.addEventListener('ended', finish);
    el.addEventListener('error', finish);
    el.play().catch(finish);
  });
}

export async function playHash(hash: string, rate = 1): Promise<boolean> {
  const url = await urlFor(hash);
  if (!url) return false;
  await playUrl(url, rate);
  return true;
}

const blobUrls = new WeakMap<Blob, string>();
export function playBlob(blob: Blob, rate = 1): Promise<void> {
  let url = blobUrls.get(blob);
  if (!url) {
    url = URL.createObjectURL(blob);
    blobUrls.set(blob, url);
  }
  return playUrl(url, rate);
}

/** Which clips a note can play: your recordings if it has any, otherwise every TTS voice. */
export function clipsFor(note: Note): string[] {
  if (note.recordings.length) return note.recordings;
  return note.tts.map((c) => c.hash);
}

export function pickClip(note: Note): string | null {
  const clips = clipsFor(note);
  if (!clips.length) return null;
  return clips[Math.floor(Math.random() * clips.length)];
}

/**
 * Last resort when a note has no audio on this device yet (added offline, say):
 * the device's own voice for the language, if it has one. `lang` is a BCP 47
 * prefix such as zh-HK.
 */
export function speakWithDevice(text: string, lang: string, rate = 1): Promise<boolean> {
  if (typeof speechSynthesis === 'undefined' || !text.trim() || !lang) return Promise.resolve(false);
  const want = lang.toLowerCase().replace('_', '-');
  const voice = speechSynthesis
    .getVoices()
    .find((v) => v.lang.toLowerCase().replace('_', '-').startsWith(want));
  if (!voice) return Promise.resolve(false);
  stop();
  const u = new SpeechSynthesisUtterance(text);
  u.voice = voice;
  u.lang = voice.lang;
  u.rate = rate;
  return new Promise((resolve) => {
    u.onend = () => resolve(true);
    u.onerror = () => resolve(false);
    speechSynthesis.speak(u);
  });
}

/** Play a specific clip, or a random one, falling back to the device voice for `lang`. */
export async function playNote(note: Note, lang: string, opts: { rate?: number; clip?: string | null } = {}) {
  const rate = opts.rate ?? 1;
  const clip = opts.clip ?? pickClip(note);
  if (clip && (await playHash(clip, rate))) return clip;
  await speakWithDevice(note.text, lang, rate);
  return null;
}

export function voiceLabel(voice: string): string {
  const m = /-([A-Za-z]+?)(Multilingual)?Neural$/.exec(voice);
  return m ? m[1] : voice;
}

// ---- recording ----

function recorderMime(): string {
  for (const t of ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/webm']) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t)) return t;
  }
  return '';
}

export function canRecord(): boolean {
  return typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
}

export class Recorder {
  private rec: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private chunks: Blob[] = [];

  async start(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
    });
    const mime = recorderMime();
    this.rec = new MediaRecorder(this.stream, mime ? { mimeType: mime } : undefined);
    this.chunks = [];
    this.rec.ondataavailable = (e) => e.data.size && this.chunks.push(e.data);
    this.rec.start();
  }

  stop(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const rec = this.rec;
      if (!rec) return reject(new Error('not recording'));
      rec.onstop = () => {
        const type = (rec.mimeType || this.chunks[0]?.type || 'audio/webm').split(';')[0];
        resolve(new Blob(this.chunks, { type }));
        this.release();
      };
      rec.stop();
    });
  }

  cancel(): void {
    if (this.rec && this.rec.state !== 'inactive') this.rec.stop();
    this.release();
  }

  private release(): void {
    // Turns the browser's microphone indicator off between takes.
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.rec = null;
  }
}

export async function sha256Hex(blob: Blob): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
